import { ScoreBreakdown } from '../core/Scoring.js';
import { Grid, GridConfig, DEFAULT_GRID_CONFIG } from '../core/Grid.js';
import {
    StorageData,
    SavedGrid,
    GameState,
    GameType,
    GAME_TYPES,
    GameOrder,
    SubmitMode,
    ChallengeRef,
    AuthoringState,
    VortexBackup,
    GAME_INITIALS,
    INITIAL_TO_GAME,
    CLASSIC_SCOPE,
    scopeToGameTypes,
    gameTypesToScope,
    gridGameSet,
    STORAGE_KEY,
    SCHEMA_VERSION,
    MAX_GRIDS
} from './types.js';
import { DEFAULT_GRIDS, isDefaultGrid } from './DefaultGrids.js';
import { scoreGameState, isVortexHubValid } from '../games/GameScoring.js';
import { VortexHub } from '../vortex/VortexHub.js';

/** Canonicalize a compact Hub string; pass legacy (';'-delimited) / empty data through. */
function canonicalHubData(data: string): string {
    if (!data || data.includes(';')) return data;
    return VortexHub.canonical(data);
}

/** Compute a config key for unlock tracking: "size/min-max" */
export function configKey(config: GridConfig): string {
    return `${config.size}/${config.min}-${config.max}`;
}

/** Smallest grid size (the Vortex Tutorial), unlocked from the start. */
export const VORTEX_TUTORIAL_SIZE = 6;
/** Challenges of a given size to clear before that size's Vortex unlocks. */
export const VORTEX_UNLOCK_CHALLENGES = 3;

export class PersistenceManager {
    private data: StorageData;
    /** Memoized Grid objects (immutable per grid) for score recomputation. */
    private gridObjCache = new Map<string, Grid>();
    /** True when the storage key was entirely absent at load (genuine first run). */
    private freshInstall = false;

    constructor() {
        this.data = this.load();
    }

    /** Deserialize (and memoize) the Grid for a saved grid — used for recompute. */
    private gridObjOf(grid: SavedGrid): Grid {
        let g = this.gridObjCache.get(grid.id);
        if (!g) {
            g = Grid.deserialize(grid.gridData);
            this.gridObjCache.set(grid.id, g);
        }
        return g;
    }

    /** Recompute a game's win/score from its serialized state (never stored). */
    private outcome(grid: SavedGrid, gameType: GameType, serialized: string | null | undefined) {
        return scoreGameState(this.gridObjOf(grid), gameType, serialized, grid.vortexGridData);
    }

    private load(): StorageData {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            // Only an absent key counts as first run — a version mismatch or corrupt
            // parse is an existing player we don't want to greet with the welcome panel.
            this.freshInstall = raw === null;
            if (raw) {
                const parsed = JSON.parse(raw) as StorageData;
                if (parsed.version === SCHEMA_VERSION) {
                    return this.reconcileDefaults(this.normalizeStoredShape(parsed));
                }
            }
        } catch {
            // Corrupted or outdated data, start fresh
        }
        return this.initializeDefault();
    }

    /** Derive a GridConfig from a grid-data string prefix ("6/0-3:..."). */
    private configFromGridData(gridData: string): GridConfig {
        const m = gridData.match(/^(\d+)\/(\d+)-(\d+):/);
        if (!m) return { ...DEFAULT_GRID_CONFIG };
        const min = parseInt(m[2], 10);
        const config: GridConfig = { size: parseInt(m[1], 10), min, max: parseInt(m[3], 10) };
        if (min === 0) config.isVortex = true;  // min === 0 ⟺ Vortex grid
        return config;
    }

    private initializeDefault(): StorageData {
        const grids: SavedGrid[] = DEFAULT_GRIDS.map((gridData, index) => ({
            id: `default-${index + 1}`,
            gridData,
            gridConfig: this.configFromGridData(gridData),  // Derive config from the grid data
            lastPlayed: 0,
            gameStates: {}
        }));

        return {
            version: SCHEMA_VERSION,
            currentGridId: grids.length > 0 ? grids[0].id : null,
            grids
        };
    }

    /**
     * Drop persisted fields that are now recomputed (scores, win flags, isDefault,
     * vortexHubValid) and convert old backup win states to serialized-only. Safe to
     * run on every load — idempotent on already-lean data.
     */
    private normalizeStoredShape(data: StorageData): StorageData {
        for (const grid of data.grids) {
            const g = grid as unknown as Record<string, unknown>;
            delete g.isDefault;
            delete g.vortexHubValid;

            for (const state of Object.values(grid.gameStates)) {
                if (!state) continue;
                const s = state as unknown as Record<string, unknown>;
                delete s.isWon;
                delete s.score;
                delete s.bestScore;
                if (s.bestSerialized == null) delete s.bestSerialized;
            }

            if (grid.vortexBackup) {
                const b = grid.vortexBackup as unknown as Record<string, unknown>;
                delete b.totalScore;
                delete b.winCount;
                const winStates = b.winStates as Record<string, unknown>;
                for (const key of Object.keys(winStates)) {
                    const w = winStates[key];
                    if (w && typeof w === 'object' && 'serialized' in w) {
                        winStates[key] = (w as { serialized: string }).serialized;
                    }
                }
                grid.vortexBackup.hubState = canonicalHubData(grid.vortexBackup.hubState);
            }

            // Canonicalize Hub data so imported / legacy saves match freshly-saved data.
            if (grid.vortexGridData) {
                grid.vortexGridData = canonicalHubData(grid.vortexGridData);
            }
        }
        return data;
    }

    private reconcileDefaults(data: StorageData): StorageData {
        // Never delete user grids here: isDefaultGrid is canonical, so a default
        // copy may be stored in a legacy serialization ("1G1,…" vs "10/1-9:1G1,…").
        // Filtering on raw-string membership would wrongly drop such copies.

        // Repair defaults whose stored config doesn't match their grid data
        // (older builds saved every default with the classic config)
        for (const g of data.grids) {
            if (isDefaultGrid(g.gridData)) g.gridConfig = this.configFromGridData(g.gridData);
        }

        // Add missing defaults (keyed by exact gridData).
        const existingDefaultData = new Set(
            data.grids.filter(g => isDefaultGrid(g.gridData)).map(g => g.gridData)
        );
        for (let i = 0; i < DEFAULT_GRIDS.length; i++) {
            if (!existingDefaultData.has(DEFAULT_GRIDS[i])) {
                data.grids.push({
                    id: `default-${i + 1}`,
                    gridData: DEFAULT_GRIDS[i],
                    gridConfig: this.configFromGridData(DEFAULT_GRIDS[i]),
                    lastPlayed: 0,
                    gameStates: {}
                });
            }
        }

        // Fix currentGridId if it pointed to a removed default
        if (data.currentGridId && !data.grids.find(g => g.id === data.currentGridId)) {
            data.currentGridId = data.grids.length > 0 ? data.grids[0].id : null;
        }

        return data;
    }

    private save(): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
        } catch {
            // Storage full or unavailable
            console.warn('Failed to save to localStorage');
        }
    }

    getData(): StorageData {
        return this.data;
    }

    /** True only on a genuinely fresh install (no prior storage key). */
    isFreshInstall(): boolean {
        return this.freshInstall;
    }

    /** Game-button ordering preference; defaults to difficulty order. */
    getGameOrder(): GameOrder {
        return this.data.preferences?.gameOrder ?? 'difficulty';
    }

    setGameOrder(order: GameOrder): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.gameOrder = order;
        this.save();
    }

    /** How challenge solutions are pushed to the leaderboard; defaults to manual. */
    getSubmitMode(): SubmitMode {
        return this.data.preferences?.submitMode ?? 'manual';
    }

    setSubmitMode(mode: SubmitMode): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.submitMode = mode;
        this.save();
    }

    /** Mirrors: amber backward hint beam visibility; defaults to off. */
    getMirrorsHintBeam(): boolean {
        return this.data.preferences?.mirrorsHintBeam ?? false;
    }

    setMirrorsHintBeam(enabled: boolean): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.mirrorsHintBeam = enabled;
        this.save();
    }

    /** Branches: teal hint rings on extendable edges; defaults to on. */
    getBranchesHintRings(): boolean {
        return this.data.preferences?.branchesHintRings ?? true;
    }

    setBranchesHintRings(enabled: boolean): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.branchesHintRings = enabled;
        this.save();
    }

    /** Line: chevron hints for start placement / next segment; defaults to on. */
    getLineHints(): boolean {
        return this.data.preferences?.lineHints ?? true;
    }

    setLineHints(enabled: boolean): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.lineHints = enabled;
        this.save();
    }

    /** Keyboard mode: crosshair cursor + keyboard placement; defaults to off. */
    getKeyboardMode(): boolean {
        return this.data.preferences?.keyboardMode ?? false;
    }

    setKeyboardMode(enabled: boolean): void {
        if (!this.data.preferences) this.data.preferences = {};
        this.data.preferences.keyboardMode = enabled;
        this.save();
    }

    /** Dev tool: full persisted blob as a JSON string. */
    exportAll(): string {
        return JSON.stringify(this.data);
    }

    /**
     * Dev tool: overwrite the persisted blob with the given JSON. Validates
     * minimally and writes it raw to storage; the caller should reload so the
     * normal load/migration path runs. Returns false on invalid input.
     */
    importAll(json: string): boolean {
        try {
            const parsed = JSON.parse(json);
            if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.grids)) return false;
            localStorage.setItem(STORAGE_KEY, json);
            return true;
        } catch {
            return false;
        }
    }

    getCurrentGridId(): string | null {
        return this.data.currentGridId;
    }

    getCurrentGrid(): SavedGrid | null {
        if (!this.data.currentGridId) return null;
        return this.data.grids.find(g => g.id === this.data.currentGridId) ?? null;
    }

    getGrid(gridId: string): SavedGrid | null {
        return this.data.grids.find(g => g.id === gridId) ?? null;
    }

    getGridsSorted(): SavedGrid[] {
        // Current grid first, then sorted by lastPlayed descending, defaults last
        const current = this.getCurrentGrid();
        const others = this.data.grids
            .filter(g => g.id !== this.data.currentGridId)
            .sort((a, b) => {
                // Defaults go to the end
                const aDef = isDefaultGrid(a.gridData), bDef = isDefaultGrid(b.gridData);
                if (aDef && !bDef) return 1;
                if (!aDef && bDef) return -1;
                // Sort by lastPlayed descending
                return b.lastPlayed - a.lastPlayed;
            });

        return current ? [current, ...others] : others;
    }

    setCurrentGrid(gridId: string): void {
        const grid = this.getGrid(gridId);
        if (grid) {
            this.data.currentGridId = gridId;
            grid.lastPlayed = Date.now();
            this.save();
        }
    }

    addGrid(gridData: string, gridConfig?: GridConfig, gameSet?: string): string | null {
        if (!this.canAddGrid()) return null;
        const id = `grid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

        // Extract config from gridData prefix if not provided
        const config = gridConfig ?? this.configFromGridData(gridData);

        const grid: SavedGrid = {
            id,
            gridData,
            gridConfig: config,
            lastPlayed: Date.now(),
            // Only stored when the set differs from the classic 12 (kept off legacy grids).
            ...(gameSet ? { gameSet } : {}),
            gameStates: {}
        };

        this.data.grids.push(grid);
        this.data.currentGridId = id;
        this.save();
        return id;
    }

    /**
     * Flag a grid as originating from the admin backlog (authoring aid, persisted).
     * Defaults are never flagged: they ship in every portfolio and are already
     * labelled as defaults, so a backlog chip on one would just be noise.
     */
    setFromBacklog(id: string): void {
        const grid = this.getGrid(id);
        if (!grid || grid.fromBacklog || isDefaultGrid(grid.gridData)) return;
        grid.fromBacklog = true;
        this.save();
    }

    /**
     * Reconcile the authoring annotations against the admin pipeline's actual state,
     * so a portfolio grid always reports the stage the backlog really has it at.
     *
     * These annotations can't self-heal from the public challenge API: a grid leaves the
     * backlog (deleted, or scheduled) with no way for the server to reach into this
     * portfolio, and a *future* challenge is deliberately absent from /challenges/recent
     * until its window opens — so its grid would otherwise sit unlabelled, or worse, keep
     * claiming to be a candidate. Only meaningful on the authoring instance.
     *
     * Authoritative in both directions (sets and clears); call only after a successful
     * fetch, never on the error path.
     */
    reconcileAuthoringState(state: AuthoringState): void {
        const inBacklog = new Set(state.candidates);
        const scheduled = new Map(state.scheduled.map(s => [s.gridData, s]));
        let changed = false;

        for (const grid of this.data.grids) {
            const isCandidate = inBacklog.has(grid.gridData);
            if (!!grid.fromBacklog !== isCandidate) {
                if (isCandidate) grid.fromBacklog = true;
                else delete grid.fromBacklog;
                changed = true;
            }

            const sched = scheduled.get(grid.gridData);
            if (sched) {
                if (grid.scheduledTitle !== sched.title || grid.scheduledStartsAt !== sched.startsAt) {
                    grid.scheduledTitle = sched.title;
                    grid.scheduledStartsAt = sched.startsAt;
                    changed = true;
                }
            } else if (grid.scheduledTitle !== undefined || grid.scheduledStartsAt !== undefined) {
                delete grid.scheduledTitle;
                delete grid.scheduledStartsAt;
                changed = true;
            }
        }
        if (changed) this.save();
    }

    /** True when this grid could be adopted into the authoring backlog (not a default, not already a candidate). */
    canAddAsCandidate(id: string): boolean {
        const grid = this.getGrid(id);
        return !!grid && !isDefaultGrid(grid.gridData) && !grid.fromBacklog;
    }

    /**
     * Ensure a challenge's grid is in the portfolio, stamped with its challenge
     * metadata. Idempotent by grid data — returns the existing grid's id if present
     * (adds a new grid otherwise). Used when the player enters a challenge.
     */
    addChallengeGrid(challenge: ChallengeRef): string | null {
        let grid = this.data.grids.find(g => g.gridData === challenge.gridData);
        if (!grid) {
            // Adding the challenge to the portfolio must not switch what the player
            // is on (boot auto-add); selecting it is what switches, via handleGridSelected.
            const prevCurrent = this.data.currentGridId;
            const id = this.addGrid(challenge.gridData);
            if (!id) return null;
            this.data.currentGridId = prevCurrent;
            grid = this.getGrid(id)!;
        }
        this.stampChallenge(grid, challenge);
        this.save();
        return grid.id;
    }

    /**
     * Reconcile challenge tags against the authoritative set of current challenges:
     * stamp grids whose data matches a current challenge, and CLEAR stale tags from
     * grids that no longer match one (e.g. an ended challenge, or a default grid that
     * was wrongly tagged by an earlier build). Adds nothing new (boot reconcile).
     * Only call this after a *successful* fetch — never when offline.
     */
    reconcileChallengeMetadata(challenges: ChallengeRef[]): void {
        const byData = new Map(challenges.map(c => [c.gridData, c]));
        let changed = false;
        for (const grid of this.data.grids) {
            const match = byData.get(grid.gridData);
            if (match && !isDefaultGrid(grid.gridData)) {
                if (this.stampChallenge(grid, match)) changed = true;
            } else if (grid.challengeId != null) {
                this.clearChallenge(grid);
                changed = true;
            }
        }
        if (changed) this.save();
    }

    /** Stamp challenge metadata onto a grid; returns true if anything changed. Defaults are never tagged. */
    private stampChallenge(grid: SavedGrid, challenge: ChallengeRef): boolean {
        if (isDefaultGrid(grid.gridData)) return false;
        if (grid.challengeId === challenge.id && grid.challengeTitle === challenge.title
            && grid.challengeScope === challenge.scope && grid.challengeEndsAt === challenge.endsAt) {
            return false;
        }
        grid.challengeId = challenge.id;
        grid.challengeTitle = challenge.title;
        grid.challengeScope = challenge.scope;
        grid.challengeEndsAt = challenge.endsAt;
        return true;
    }

    private clearChallenge(grid: SavedGrid): void {
        delete grid.challengeId;
        delete grid.challengeTitle;
        delete grid.challengeScope;
        delete grid.challengeEndsAt;
        delete grid.challengeSubmitted;
    }

    /** True when the grid is linked to a server challenge. */
    isChallengeGrid(gridId: string): boolean {
        return this.getGrid(gridId)?.challengeId != null;
    }

    /** Record the solution_data last accepted by the leaderboard for this grid. */
    markChallengeSubmitted(gridId: string, solutionData: string): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;
        grid.challengeSubmitted = solutionData;
        this.save();
    }

    /**
     * A grid is deletable unless it's the last remaining copy of a default grid's
     * data — at least one copy of each default must always remain.
     */
    canDeleteGrid(gridId: string): boolean {
        const grid = this.getGrid(gridId);
        if (!grid) return false;
        if (!isDefaultGrid(grid.gridData)) return true;
        const copies = this.data.grids.filter(g => g.gridData === grid.gridData).length;
        return copies > 1;
    }

    deleteGrid(gridId: string): boolean {
        const grid = this.getGrid(gridId);
        if (!grid || !this.canDeleteGrid(gridId)) return false;

        const index = this.data.grids.findIndex(g => g.id === gridId);
        if (index === -1) return false;

        this.data.grids.splice(index, 1);
        this.gridObjCache.delete(gridId);

        // If we deleted the current grid, switch to another
        if (this.data.currentGridId === gridId) {
            const remaining = this.getGridsSorted();
            this.data.currentGridId = remaining.length > 0 ? remaining[0].id : null;
        }

        this.save();
        return true;
    }

    /**
     * A custom grid: one the player generated or imported, as opposed to a shipped
     * default or a challenge-linked grid. These are the only grids that count against
     * the budget and the only ones "Remove empty grids" touches — defaults and
     * challenges are kept free. (Matches the "Custom Grids" folder in the selector.)
     */
    private isCustomGrid(grid: SavedGrid): boolean {
        return !isDefaultGrid(grid.gridData) && grid.challengeId == null;
    }

    /** Number of grids counting against the budget: custom grids only. */
    private budgetUsage(): number {
        return this.data.grids.filter(g => this.isCustomGrid(g)).length;
    }

    /** Whether another grid can be added without exceeding the budget. */
    canAddGrid(): boolean {
        return this.budgetUsage() < MAX_GRIDS;
    }

    /** A grid with no pieces placed and no Hub edits. */
    private isGridEmpty(grid: SavedGrid): boolean {
        if (grid.vortexGridData) return false;
        return Object.values(grid.gameStates).every(s => !s || (!s.serialized && !s.bestSerialized));
    }

    /**
     * Delete every empty custom grid (no progress), keeping the current grid. Defaults
     * and challenge grids are never touched, even when empty. Returns how many were removed.
     */
    removeEmptyGrids(): number {
        const ids = this.data.grids
            .filter(g => g.id !== this.data.currentGridId && this.isCustomGrid(g) && this.isGridEmpty(g))
            .map(g => g.id);
        let removed = 0;
        for (const id of ids) {
            if (this.deleteGrid(id)) removed++;  // deleteGrid enforces the default guard
        }
        return removed;
    }

    /** Store the current serialized state (best is preserved). */
    saveGameState(gridId: string, gameType: GameType, serialized: string): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;

        const existing = grid.gameStates[gameType];
        grid.gameStates[gameType] = { serialized, bestSerialized: existing?.bestSerialized };

        grid.lastPlayed = Date.now();
        this.save();
    }

    /**
     * Record a winning state as the new best if it scores better than the stored
     * best (lower totalScore is better). The candidate's score is supplied by the
     * caller (which already has it live); the stored best is recomputed.
     *
     * Returns whether it actually became the new best — the caller can't tell from
     * the outside without recomputing the same comparison, and the UI marks the
     * moment a best improves.
     */
    saveBestScore(
        gridId: string, gameType: GameType, serialized: string, score: ScoreBreakdown
    ): boolean {
        const grid = this.getGrid(gridId);
        if (!grid) return false;

        const existing = grid.gameStates[gameType];
        const existingBest = this.outcome(grid, gameType, existing?.bestSerialized).score;
        if (existingBest && existingBest.totalScore <= score.totalScore) return false;

        if (existing) {
            existing.bestSerialized = serialized;
        } else {
            grid.gameStates[gameType] = { serialized, bestSerialized: serialized };
        }
        this.save();
        return true;
    }

    getGameState(gridId: string, gameType: GameType): GameState | null {
        const grid = this.getGrid(gridId);
        if (!grid) return null;
        return grid.gameStates[gameType] ?? null;
    }

    /** Recompute the best (serialized + score) for a game, or null if none/invalid. */
    getBestScore(gridId: string, gameType: GameType): { serialized: string; score: ScoreBreakdown } | null {
        const grid = this.getGrid(gridId);
        const best = grid?.gameStates[gameType]?.bestSerialized;
        if (!grid || !best) return null;
        const { score } = this.outcome(grid, gameType, best);
        return score ? { serialized: best, score } : null;
    }

    /** Recompute the total score: sum of best scores if every game in the grid's set is
     *  won (and, for Vortex, the Hub is valid); else null. */
    getTotalScore(gridId: string): number | null {
        const grid = this.getGrid(gridId);
        if (!grid) return null;

        // Vortex grids require a valid Hub for completion
        if (grid.gridConfig?.isVortex && !isVortexHubValid(this.gridObjOf(grid), grid.vortexGridData)) {
            return null;
        }

        let total = 0;
        for (const gameType of gridGameSet(grid)) {
            const { score } = this.outcome(grid, gameType, grid.gameStates[gameType]?.bestSerialized);
            if (!score) return null;  // not all solved
            total += score.totalScore;
        }
        return total;
    }

    clearGameState(gridId: string, gameType: GameType): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;

        const existing = grid.gameStates[gameType];
        if (existing) existing.serialized = '';  // keep best

        this.save();
    }

    exportGridWithSolutions(gridId: string): string | null {
        const grid = this.getGrid(gridId);
        if (!grid) return null;

        // Check all games are complete
        const totalScore = this.getTotalScore(gridId);
        if (totalScore === null) return null;

        // Build export string: gridData;A:state;B:state;... (only games in the grid's set)
        let result = grid.gridData;

        for (const gameType of gridGameSet(grid)) {
            const initial = GAME_INITIALS[gameType];
            const state = grid.gameStates[gameType];
            const bestSerialized = state?.bestSerialized ?? '';
            result += `;${initial}:${bestSerialized}`;
        }

        // Append Vortex Hub data if present
        if (grid.vortexGridData) {
            // Normalize legacy G:/P:/R: format to compact format
            const hubData = grid.vortexGridData.includes(';')
                ? PersistenceManager.hubNativeToCompact(grid.vortexGridData)
                : grid.vortexGridData;
            result += `;V:${hubData}`;
        }

        return result;
    }

    /**
     * Partial-friendly solution_data for a played grid: only the games that are
     * currently won (best score valid), joined as `A:state;C:state;…`, plus the
     * Vortex Hub (`V:…`) when present. Returns null if nothing is won yet.
     *
     * Mirrors the draft format that the solver stores for Quadrant, so a curator's
     * local play can be captured as a Gyrodiot reference draft (admin only).
     */
    exportSolvedStates(gridId: string): { data: string; solved: number } | null {
        const grid = this.getGrid(gridId);
        if (!grid) return null;

        const parts: string[] = [];
        for (const gameType of gridGameSet(grid)) {
            const best = this.getBestScore(gridId, gameType);
            if (best) parts.push(`${GAME_INITIALS[gameType]}:${best.serialized}`);
        }
        if (parts.length === 0) return null;

        const solved = parts.length;
        if (grid.vortexGridData) {
            const hubData = grid.vortexGridData.includes(';')
                ? PersistenceManager.hubNativeToCompact(grid.vortexGridData)
                : grid.vortexGridData;
            parts.push(`V:${hubData}`);
        }
        return { data: parts.join(';'), solved };
    }

    parseImportString(exportString: string): { gridData: string; gridConfig: GridConfig; gameStates: Record<string, string>; vortexHubData?: string; gameSet?: string } | null {
        const trimmed = exportString.trim();
        if (!trimmed) return null;

        const parts = trimmed.split(';');
        const gridData = parts[0];

        // Parse grid config from prefix if present, otherwise use legacy defaults
        let config: GridConfig;
        let entriesStr: string;

        const configMatch = gridData.match(/^(\d+)\/(\d+)-(\d+):(.*)$/);
        if (configMatch) {
            config = {
                size: parseInt(configMatch[1], 10),
                min: parseInt(configMatch[2], 10),
                max: parseInt(configMatch[3], 10),
            };
            entriesStr = configMatch[4];
        } else {
            // Legacy format: assume 10/1-9
            config = { ...DEFAULT_GRID_CONFIG };
            entriesStr = gridData;
        }

        // Only min=0 (Vortex) and min=1 (Base) are valid
        if (config.min !== 0 && config.min !== 1) return null;
        if (config.min === 0) config.isVortex = true;

        // Validate digit count matches config
        const expectedDigitCount = config.max - config.min + 1;
        const digitEntries = entriesStr.split(',');
        if (digitEntries.length !== expectedDigitCount) return null;

        // Build dynamic regex based on config
        const maxColLetter = String.fromCharCode('A'.charCodeAt(0) + config.size - 1);
        const digitPattern = config.min === 0 ? `[0-${config.max}]` : `[${config.min}-${config.max}]`;
        const entryRegex = new RegExp(`^${digitPattern}[A-${maxColLetter}][0-${config.size - 1}]$`);

        // Validate each entry format
        for (const entry of digitEntries) {
            if (!entryRegex.test(entry)) return null;
        }

        // Parse game states and Vortex Hub data
        const gameStates: Record<string, string> = {};
        let vortexHubData: string | undefined;
        let gameSet: string | undefined;
        for (let i = 1; i < parts.length; i++) {
            const part = parts[i];
            const colonIndex = part.indexOf(':');
            if (colonIndex === -1) continue;

            const initial = part.substring(0, colonIndex);
            const serialized = part.substring(colonIndex + 1);

            if (initial === 'V') {
                vortexHubData = serialized;
                config.isVortex = true;
            } else if (initial === 'G') {
                // Challenge code: the game set to play this grid with. Round-tripped
                // through the scope helpers so a malformed or short set is rejected
                // rather than silently shrinking the grid's game count.
                const parsedSet = scopeToGameTypes(serialized);
                if (parsedSet.length === CLASSIC_SCOPE.length) gameSet = gameTypesToScope(parsedSet);
            } else if (INITIAL_TO_GAME[initial]) {
                gameStates[initial] = serialized;
            }
        }

        return { gridData, gridConfig: config, gameStates, vortexHubData, gameSet };
    }

    addImportedGrid(gridData: string, gameStates: Partial<Record<GameType, GameState>>, isImported: boolean, gridConfig?: GridConfig, gameSet?: string): string | null {
        if (!this.canAddGrid()) return null;
        const id = `${isImported ? 'imported' : 'grid'}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const grid: SavedGrid = {
            id,
            gridData,
            gridConfig: gridConfig ?? { ...DEFAULT_GRID_CONFIG },
            lastPlayed: Date.now(),
            isImported: isImported || undefined,
            gameSet: gameSet && gameSet !== CLASSIC_SCOPE ? gameSet : undefined,
            gameStates
        };

        this.data.grids.push(grid);
        this.data.currentGridId = id;
        this.save();
        return id;
    }

    /**
     * Copy a grid into a fresh native grid (challenge/backlog/imported linkage all dropped).
     *
     * `gameSet` overrides the set the copy plays — this is the point of the duplicate flow:
     * re-roll the set of a grid you've partly solved. With `copyScores`, the source's game
     * states carry over, but only for games that are **in the new set** — a game swapped out
     * has nowhere to live. On a Vortex grid the Hub comes along too, since the copied
     * solutions are only valid against the same gate layout.
     */
    duplicateGrid(
        gridId: string,
        opts: { gameSet?: string; copyScores?: boolean } = {},
    ): string | null {
        const sourceGrid = this.getGrid(gridId);
        if (!sourceGrid || !this.canAddGrid()) return null;

        const id = `grid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const gameSet = opts.gameSet && opts.gameSet !== CLASSIC_SCOPE ? opts.gameSet : undefined;
        const isVortex = sourceGrid.gridConfig?.isVortex === true;

        // "Keep solved games" carries the best solutions found across ALL same-layout grids,
        // not just the source (see carrySolutions): the best-scoring copy of each game for a
        // Base grid; the best donor's Hub + its solutions, as a set, for a Vortex grid.
        const gameStates: Partial<Record<GameType, GameState>> = {};
        let carriedHub: string | undefined;
        if (opts.copyScores) {
            const carry = this.carrySolutions(sourceGrid.gridData, isVortex, scopeToGameTypes(gameSet ?? CLASSIC_SCOPE));
            Object.assign(gameStates, carry.gameStates);
            carriedHub = carry.vortexGridData;
        }

        const grid: SavedGrid = {
            id,
            gridData: sourceGrid.gridData,
            gridConfig: sourceGrid.gridConfig ? { ...sourceGrid.gridConfig } : { ...DEFAULT_GRID_CONFIG },
            lastPlayed: Date.now(),
            gameSet,
            gameStates
        };
        // Remember a challenge/default origin so the (native) copy still says where it came
        // from in Custom Grids; propagate an existing tag so a copy-of-a-copy keeps it.
        const copyOf = this.copyOrigin(sourceGrid);
        if (copyOf) grid.copyOf = copyOf;
        if (carriedHub !== undefined) grid.vortexGridData = carriedHub;

        this.data.grids.push(grid);
        this.data.currentGridId = id;
        this.save();
        return id;
    }

    /** Grids sharing a digit layout *and* Vortex-ness — the pool a duplicate carries from. */
    private sameLayoutGrids(gridData: string, isVortex: boolean): SavedGrid[] {
        return this.data.grids.filter(
            g => g.gridData === gridData && (g.gridConfig?.isVortex === true) === isVortex,
        );
    }

    /**
     * Every game solved on some same-layout grid (matching Vortex-ness) — the superset a
     * "keep solved games" duplicate could draw from, regardless of the chosen set. Drives
     * whether the modal offers the carry toggle, and its "dropping …" line.
     */
    carriableGames(gridData: string, isVortex: boolean): GameType[] {
        const found = new Set<GameType>();
        for (const g of this.sameLayoutGrids(gridData, isVortex)) {
            for (const gameType of GAME_TYPES) {
                if (this.getBestScore(g.id, gameType)) found.add(gameType);
            }
        }
        return GAME_TYPES.filter(g => found.has(g));
    }

    /**
     * The solutions a "keep solved games" duplicate carries into the copy, drawn from all
     * same-layout grids (not just the source). Base: the best-scoring copy of each in-set
     * game, cherry-picked across grids. Vortex: the single best donor grid's Hub plus its
     * in-set solutions — Vortex solutions are only valid against their own Hub, so they can't
     * be mixed across Hubs; the donor is the one whose intersection with the chosen set is
     * largest (ties broken by best total score).
     */
    carrySolutions(
        gridData: string, isVortex: boolean, scope: GameType[],
    ): { gameStates: Partial<Record<GameType, GameState>>; vortexGridData?: string } {
        const gameStates: Partial<Record<GameType, GameState>> = {};

        if (isVortex) {
            const donor = this.bestVortexDonor(gridData, scope);
            if (!donor) return { gameStates };
            for (const gameType of scope) {
                const best = this.getBestScore(donor.id, gameType);
                if (best) gameStates[gameType] = { serialized: best.serialized, bestSerialized: best.serialized };
            }
            return { gameStates, vortexGridData: donor.vortexGridData };
        }

        for (const gameType of scope) {
            let best: { serialized: string; score: ScoreBreakdown } | null = null;
            for (const g of this.sameLayoutGrids(gridData, false)) {
                const b = this.getBestScore(g.id, gameType);
                if (b && (!best || b.score.totalScore < best.score.totalScore)) best = b;
            }
            if (best) gameStates[gameType] = { serialized: best.serialized, bestSerialized: best.serialized };
        }
        return { gameStates };
    }

    /**
     * The Vortex grid whose solved games best cover the chosen set: most in-set games solved,
     * ties broken by lowest total score. Its Hub and solutions carry as a set.
     */
    private bestVortexDonor(gridData: string, scope: GameType[]): SavedGrid | null {
        let best: SavedGrid | null = null;
        let bestCount = 0;
        let bestScore = Infinity;
        for (const g of this.sameLayoutGrids(gridData, true)) {
            if (g.vortexGridData === undefined) continue;
            let count = 0;
            let total = 0;
            for (const gameType of scope) {
                const b = this.getBestScore(g.id, gameType);
                if (b) { count++; total += b.score.totalScore; }
            }
            if (count > bestCount || (count === bestCount && count > 0 && total < bestScore)) {
                best = g;
                bestCount = count;
                bestScore = total;
            }
        }
        return best;
    }

    /**
     * The origin tag a copy of this grid should carry: the source's own tag if it already
     * has one (so provenance survives repeated copies), else the challenge title when the
     * source is a challenge, else nothing. A copy is native and lands in Custom Grids, where
     * a challenge origin would otherwise be lost — a default origin needs no tag, since a
     * copy keeps the default's grid code and stays classified as a default grid anyway.
     */
    private copyOrigin(source: SavedGrid): string | undefined {
        if (source.copyOf) return source.copyOf;
        if (source.challengeId != null) return source.challengeTitle ?? 'Challenge';
        return undefined;
    }

    /**
     * A "challenge code": the grid code plus the grid's game set, as `<gridcode>;G:<scope>`.
     * Shares both the grid *and* which 12 games to play it with, which a bare grid code
     * can't express. `G` is not a game initial, and {@link parseImportString} ignores
     * unknown parts, so older builds read this as a plain grid code (classic 12).
     */
    exportChallengeCode(gridId: string): string | null {
        const grid = this.getGrid(gridId);
        if (!grid) return null;
        return `${grid.gridData};G:${gameTypesToScope(gridGameSet(grid))}`;
    }

    // ── Vortex ────────────────────────────────────────────────────

    /**
     * Whether a Vortex config is unlocked for the grid creator / import.
     * Admin builds unlock everything. Otherwise the smallest size (Vortex Tutorial)
     * is open from the start; larger sizes unlock after clearing
     * VORTEX_UNLOCK_CHALLENGES challenges of that size.
     */
    isVortexUnlocked(vortexConfig: GridConfig): boolean {
        if (import.meta.env?.VITE_ADMIN) return true;
        if (vortexConfig.size <= VORTEX_TUTORIAL_SIZE) return true;
        return this.getChallengeClears(vortexConfig.size) >= VORTEX_UNLOCK_CHALLENGES;
    }

    /** Challenges the player has cleared at a given grid size (drives Vortex unlock). */
    getChallengeClears(size: number): number {
        return this.data.challengeClears?.[size] ?? 0;
    }

    /**
     * Whether the player has any saved Vortex progress: a Vortex grid carrying
     * placed pieces or Hub edits. Drives the one-time Vortex welcome, which greets
     * a player the first time they open a Vortex grid with nothing played yet.
     */
    hasSavedVortexProgress(): boolean {
        return this.data.grids.some(g => g.gridConfig?.isVortex && !this.isGridEmpty(g));
    }

    /**
     * Record a cleared challenge (all scope games won), bumping the per-size counter
     * that gates the Vortex creator. Deduped by challenge id so re-completions and
     * re-submissions don't inflate the count.
     */
    recordChallengeCleared(size: number, challengeId: number): void {
        if (!this.data.clearedChallengeIds) this.data.clearedChallengeIds = [];
        if (this.data.clearedChallengeIds.includes(challengeId)) return;
        this.data.clearedChallengeIds.push(challengeId);
        if (!this.data.challengeClears) this.data.challengeClears = {};
        this.data.challengeClears[size] = (this.data.challengeClears[size] ?? 0) + 1;
        this.save();
    }

    /** Save Vortex Hub data for a grid. (Hub validity is recomputed, not stored.) */
    saveVortexGridData(gridId: string, vortexData: string): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;
        grid.vortexGridData = canonicalHubData(vortexData);
        this.save();
    }

    /** Recompute whether a grid's Vortex Hub is valid. */
    isHubValid(gridId: string): boolean {
        const grid = this.getGrid(gridId);
        if (!grid) return false;
        return isVortexHubValid(this.gridObjOf(grid), grid.vortexGridData);
    }

    /** Get Vortex Hub data for a grid. */
    getVortexGridData(gridId: string): string | null {
        const grid = this.getGrid(gridId);
        return grid?.vortexGridData ?? null;
    }

    /** Save Vortex backup for a grid. */
    saveVortexBackup(gridId: string, backup: VortexBackup): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;
        grid.vortexBackup = { ...backup, hubState: canonicalHubData(backup.hubState) };
        this.save();
    }

    /** Get Vortex backup for a grid. */
    getVortexBackup(gridId: string): VortexBackup | null {
        const grid = this.getGrid(gridId);
        return grid?.vortexBackup ?? null;
    }

    /** Force-set the best (and current) serialized state — used by backup restore. */
    saveBestScoreForced(gridId: string, gameType: GameType, serialized: string): void {
        const grid = this.getGrid(gridId);
        if (!grid) return;

        const existing = grid.gameStates[gameType];
        if (existing) {
            existing.bestSerialized = serialized;
        } else {
            grid.gameStates[gameType] = { serialized, bestSerialized: serialized };
        }
        this.save();
    }

    // ── Vortex Hub format migration ────────────────────────────────

    /** Convert legacy VortexHub format (G:...;P:...;R:...) to compact format. */
    static hubNativeToCompact(nativeData: string): string {
        const allGates = new Set<string>();
        const paths: string[][] = [];
        const unlinkedPairs: string[] = [];

        for (const section of nativeData.split(';')) {
            if (section.startsWith('G:')) {
                for (const pos of section.slice(2).split(',')) {
                    if (pos) allGates.add(pos);
                }
            } else if (section.startsWith('P:')) {
                for (const pathStr of section.slice(2).split(',')) {
                    if (pathStr) paths.push(pathStr.split('.'));
                }
            } else if (section.startsWith('R:')) {
                for (const pair of section.slice(2).split(',')) {
                    if (pair) unlinkedPairs.push(pair);
                }
            }
        }

        // Gates covered by paths or unlinked pairings
        const coveredGates = new Set<string>();
        for (const path of paths) {
            coveredGates.add(path[0]);
            coveredGates.add(path[path.length - 1]);
        }
        for (const pair of unlinkedPairs) {
            const eqIdx = pair.indexOf('=');
            if (eqIdx >= 0) {
                coveredGates.add(pair.slice(0, eqIdx));
                coveredGates.add(pair.slice(eqIdx + 1));
            }
        }

        const entries: string[] = [];
        for (const path of paths) entries.push(path.join(''));
        for (const pair of unlinkedPairs) entries.push(pair);
        for (const gate of allGates) {
            if (!coveredGates.has(gate)) entries.push(gate);
        }

        return entries.join(',');
    }
}
